/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MenuContext } from 'App';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash/cloneDeep';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { findEqclIdList, findProjectById, saveIssue } from 'apis/setup/issue/IssueSetup';
import { Code } from 'models/common/CommonCode';
import { ManagementMode, PageMode } from 'models/common/Common';
import { MenuContextType } from 'models/admin/Menu';
import { IssueSetup } from 'models/setup/issue/issue';
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Checkbox,
} from '@mui/material';
import { tb } from 'components/layouts/Table';
import { GlobalBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomDatepicker from 'components/inputs/CustomDatepicker';
import { ComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import SetupEquipmentPopUp from '../common/SetupEquipmentPopUp';
import { SetupEquipment } from 'models/setup/SetupEquipment';
import CustomEditorInputWithSearch from 'components/inputs/CustomEditorInputWithSearch';
import CrossEditor from 'components/crosseditor/CrossEditor';
import { CommonUtil } from 'utils/CommonUtil';
import { findProjectListForExcp } from 'apis/setup/excp/Item';
import Dropzone from 'components/dropzone/Dropzone';
import { FileSaveResult } from 'models/admin/FileInfo';
import FileGrid from 'components/dropzone/FileGrid';
import useSessionStore from 'stores/useSessionStore';
import { SetupExcpParam } from 'models/setup/excp/excp';
import { st } from 'components/inputs/CustomInput';
import IssueConfirmPopup from './IssueConfirmPopup';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import CustomDialog from 'components/modals/common/CustomDialog';
import { CrossEditorInfo } from 'models/common/CrossEditor';
import moment from 'moment';
import { findDisplayProject } from 'apis/setup/SetupProject';

interface IssueRegistProps {
  mode: PageMode;
  isuId?: string;
  excpId?: string;
  elmPrjId?: string;
  open: boolean;
  close: () => void;
  onSearch?: () => void;
}

const ProjectIssueCreatePage = (props: IssueRegistProps) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const { userId, empNm, deptCd, deptNm } = useSessionStore();
  const menuContext = useContext<MenuContextType>(MenuContext);

  const dropzoneRef = useRef<any>();
  const descEditorRef = useRef<CrossEditor>(null);
  const iprvMesrEditorRef = useRef<CrossEditor>(null);

  const [isuDtl, setIsuDtl] = useState<string>('');
  const [isuProg, setIsuProg] = useState<string>('');
  const [cmnGrCd, setCmnGrCd] = useState<string>('');
  const [rvwRltCtn, setRvwRltCtn] = useState<string>('');
  const [isOpenEquipmentModal, setOpenEquipmentModal] = useState<boolean>(false);
  const [isClickConfirm, setIsClickConfirm] = useState<boolean>(false);
  const [isDescEditorReady, setIsDescEditorReady] = useState(false); //editor 실행 여부
  const [isIprwEditorReady, setIsIprwEditorReady] = useState(false);

  const [isuTpCd, setIsuTpCd] = useState<Code[]>([]);
  const [isuOccrCd, setIsuOccrCd] = useState<Code[]>([]);
  const [filteredIsuOccrCd, setFilteredIsuOccrCd] = useState<Code[]>([]);
  const [isuLocCd, setIsuLocCd] = useState<Code[]>([]);
  const [filteredIsuLocCd, setFilteredIsuLocCd] = useState<Code[]>([]);
  const [isuTpLvCd, setIsuTpLvCd] = useState<Code[]>([]);
  const [cmnGrCdList, setCmnGrCdList] = useState<Code[]>([]);

  const [searchParam, setSearchParam] = useState<SetupExcpParam>();
  const [originMasterData, setOriginMasterData] = useState<IssueSetup>({});
  const [createData, setCreateData] = useState<IssueSetup>({});
  const [prjMasterData, setPrjMasterData] = useState<IssueSetup[]>([]);
  const [prjExcpData, setPrjExcpData] = useState<IssueSetup[]>([]);

  //콤보박스 reset key
  const [excpResetKey, setExcpResetKey] = useState(0);

  const [isuId, setIsuId] = useState<string>(props.isuId ?? '');
  const [pageMode, setPageMode] = useState<PageMode>(props.mode ?? 'WRITE');
  const [errors, setErrors] = useState<any>({});

  const [eqpNmYn, setEqpNmYn] = useState<boolean>(false);

  // title setting
  const getTitle = () => {
    if (!createData.isuId) return t('com.label.프로젝트 이슈 등록', '프로젝트 이슈 등록');
    else if (originMasterData.isuStatCd !== 'CPLT')
      return t('com.label.프로젝트 이슈 등록', '프로젝트 이슈 수정');
    else return t('com.label.프로젝트 이슈 완료', '프로젝트 이슈 완료');
  };

  //협력사 계정 체크
  const isEmailFormat = () => {
    if (!userId) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(userId);
  };

  //공통코드 호출
  const initCodes = async () => {
    const isuTpCd = await getCommonCodeNames('ISU_TP_CD');
    const isuOccrCd = await getCommonCodeNames('ISU_OCCR_DIVS_CD');
    const isuLocCd = await getCommonCodeNames('ISU_OCCR_LOC_CD');
    const isuTpLvCd = await getCommonCodeNames('ISU_TP_LV_CD');
    const prjSinTpl = await getCommonCodeNames('PRJ_SIN_TPL');

    setIsuTpCd(isuTpCd);
    setIsuOccrCd(isuOccrCd);
    setIsuLocCd(isuLocCd);
    setIsuTpLvCd(isuTpLvCd);

    const defaultIsuDesc = prjSinTpl.find((item) => item.cmnCd === 'ISU-DTL')?.optValCtn1 || '';
    const defaultIsuProg = prjSinTpl.find((item) => item.cmnCd === 'ISU-PROG')?.optValCtn1 || '';
    setIsuDtl(defaultIsuDesc);
    setIsuProg(defaultIsuProg);
  };

  useEffect(() => {
    if (pageMode === PageMode.WRITE && isDescEditorReady && isIprwEditorReady) {
      handleEditorOnLoad(descEditorRef, isuDtl);
      handleEditorOnLoad(iprvMesrEditorRef, isuProg);
      setTimeout(() => {
        setCreateData((prev) => ({
          ...prev,
          prjIsuDesc: isuDtl,
          isuIprvMesrCtn: isuProg,
        }));
      }, 100);
    }
  }, [isuDtl, isuProg, isDescEditorReady, isIprwEditorReady]);

  useEffect(() => {
    const optIsuOccrCd = isuOccrCd.filter((d) =>
      isuTpLvCd.some((m) => m.optValCtn1 === createData.isuTpCd && m.optValCtn2 === d.cmnCd)
    );
    setFilteredIsuOccrCd(optIsuOccrCd);

    const optIsuLocCd = isuLocCd.filter((d) =>
      isuTpLvCd.some(
        (m) =>
          m.optValCtn1 === createData.isuTpCd &&
          m.optValCtn2 === createData.isuOccrDivsCd &&
          m.optValCtn3 === d.cmnCd
      )
    );
    setFilteredIsuLocCd(optIsuLocCd);
  }, [createData.isuTpCd, createData.isuOccrDivsCd, isuTpLvCd]);

  //프로젝트 옵션 정보 조회 (접근 )
  const findprojectList = async () => {
    const result = await findDisplayProject();
    if (result) {
      setPrjMasterData(result);
    } else {
      openMessageBar({
        type: 'error',
        content: t(
          'com.label.요청 정보 조회 중 오류가 발생했습니다.',
          '요청 정보 조회 중 오류가 발생했습니다.'
        ),
      });
    }
  };

  //대상설비에 따른 셋업 단계
  const getCmnGrCdList = async () => {
    const getCmnGrCdOpt = await getCommonCodeNames(cmnGrCd);
    setCmnGrCdList(getCmnGrCdOpt);
  };

  const loadProject = async (isuId) => {
    await findProjectById(isuId).then((result: IssueSetup) => {
      if (!result) {
        openMessageBar({
          type: 'error',
          content: t('setup.msg.프로젝트 정보가 없습니다.', '프로젝트 정보가 없습니다.'),
        });
        menuContext.closeCurrentTab(menuContext);
        return;
      }
      setOriginMasterData(cloneDeep(result));
      setCreateData(cloneDeep(result));
    });
  };

  //대상 설비
  const findEqclId = async (eqclId) => {
    if (!eqclId) {
      setCmnGrCd('');
      return;
    }
    findEqclIdList(eqclId).then((result: IssueSetup) => {
      if (!result) {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
        return;
      }
      setCmnGrCd(result?.cmnGrCd || '');
    });
  };

  //프로젝트명
  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.elmPrjId,
          cmnCdNm: cur.elmPrjNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjMasterData]);

  //프로젝트 협력사
  const optionsPrjExcp = useMemo(() => {
    return (prjExcpData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.excpId,
          cmnCdNm: cur.excpNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjExcpData]);

  //플랜트 찾기
  const checkPlantCd = (prjId) => {
    const plantCd = prjMasterData.find((item) => item.elmPrjId === prjId);
    setCreateData((prev) => ({
      ...prev,
      bildPlntCd: plantCd?.bildPlntCd ?? '',
      bildPlntCdNm: plantCd?.bildPlntCdNm ?? '',
    }));
  };

  //대상 설비 팝업
  const handleOpenEquipmentModal = () => {
    if (!createData.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('setup.label.프로젝트명을 입력해 주세요.', '프로젝트명을 입력해 주세요.'),
      });
      return;
    }
    setOpenEquipmentModal(true);
  };

  const isEditable = () => {
    if (pageMode === PageMode.WRITE || pageMode === PageMode.EDIT) {
      return true;
    } else {
      return false;
    }
  };
  const isModify = () => {
    return (
      (originMasterData.isuStatCd === 'SAVE' && originMasterData.dataInsUserId === userId) ||
      originMasterData.isuStatCd !== 'SAVE'
    );
  };

  const dateFormat = (date?: string) => {
    if (!date) return '';
    return dayjs(date, 'YYYYMMDD').format('YYYY.MM.DD');
  };

  //textArea handler
  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setCreateData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleItemChange = (name: string, value: string | string[]) => {
    setCreateData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const checkError = () => {
    const err = {};
    if (!createData.elmPrjId) {
      Object.assign(err, { elmPrjId: true });
    }
    if (!createData.tgtEqpId) {
      Object.assign(err, { tgtEqpId: true });
    }
    if (!createData.isuTpCd) {
      Object.assign(err, { isuTpCd: true });
    }
    if (!createData.excpId) {
      Object.assign(err, { excpId: true });
    }
    if (!createData.isuOccrDivsCd) {
      Object.assign(err, { isuOccrDivsCd: true });
    }
    if (!createData.isuTitNm) {
      Object.assign(err, { isuTitNm: true });
    }
    if (createData.prjIsuDesc === CrossEditorInfo.INIT_VALUE || createData.prjIsuDesc === isuDtl) {
      Object.assign(err, { prjIsuDesc: true });
    }
    if (createData.isuTpCd !== 'MINT') {
      if (!createData.cpltExpcDt) {
        Object.assign(err, { cpltExpcDt: true });
      }
    }
    if (!createData.prjSpCd && !eqpNmYn && originMasterData.eqpNm !== 'COMMON') {
      Object.assign(err, { prjSpCd: true });
    }
    if (!createData.isuOccrLocCd) {
      Object.assign(err, { isuOccrLocCd: true });
    }
    setErrors(err);
    if (!Object.keys(err).filter((k) => err[k]).length) {
      return true;
    }
    openMessageBar({
      type: 'error',
      content: t('setup.label.입력값을 확인해주세요.', '입력값을 확인해주세요.'),
    });
    return false;
  };

  //CPLT : 완료 / SAVE : 임시저장 / REG_CPLT: 등록완료(저장)
  const btnSave = useCallback(
    async (mode: 'CPLT' | 'SAVE' | 'REG_CPLT' | 'DELETE') => {
      if (mode !== 'DELETE' && !checkError()) {
        return;
      }
      let atchFileGrId;
      if (mode == 'CPLT') {
        atchFileGrId = createData.atchFileGrId; // 파일 첨부
      } else {
        atchFileGrId = await handleFileSave();
      }
      let saveRowsData = createData;
      saveRowsData = {
        ...saveRowsData, // 이전 값 복사
        atchFileGrId: atchFileGrId || '',
        saveType: mode,
        editMode: mode === 'DELETE' ? PageMode.DELETE : mode === 'CPLT' ? PageMode.EDIT : pageMode,
      };
      openCommonModal({
        modalType: 'confirm',
        content:
          mode !== 'DELETE'
            ? t('com.label.저장하시겠습니까?', '저장하시겠습니까?')
            : t('com.label.삭제하시겠습니까?', '삭제하시겠습니까?'),
        yesCallback: async () => {
          const response = await saveIssue({
            ...saveRowsData,
          });
          if (response == null || response?.successOrNot === 'N') {
            if (response?.statusCode === 'PARAMETER_VALUE_ERROR') {
              openMessageBar({
                type: 'error',
                content: t(
                  'setup.label.등록된 공통 설비가 없습니다.',
                  '등록된 공통 설비가 없습니다.'
                ),
              });
            } else {
              openMessageBar({
                type: 'error',
                content:
                  mode !== 'DELETE'
                    ? t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.')
                    : t('com.label.삭제 중 오류가 발생했습니다.', '삭제 중 오류가 발생했습니다.'),
              });
            }
            return;
          }
          openMessageBar({
            type: 'confirm',
            content:
              mode !== 'DELETE'
                ? t('com.label.저장되었습니다.', '저장되었습니다.')
                : t('com.label.삭제되었습니다.', '삭제되었습니다.'),
          });
          if (response.data) {
            if (mode !== 'DELETE') {
              if (mode === 'CPLT') {
                setOriginMasterData(saveRowsData);
                await loadProject(isuId);
              }
              setIsClickConfirm(false);
              setEqpNmYn(false);
              setPageMode(PageMode.READ);
              setIsuId(response.data ?? '');
            } else {
              props.close();
              props.onSearch;
            }
          }
        },
      });
    },
    [createData]
  );

  const btnCancel = () => {
    setPageMode(PageMode.READ);
  };

  //text editor
  const handleEditorOnLoad = (ref, value) => {
    if (!ref.current?.editorRef) return;
    ref.current?.editorRef.SetBodyValue(CommonUtil.safeHtmlContents(value ?? '', false));
    //editor 로딩 끝나면 Ready -> true로 변환
    if (ref === descEditorRef) setIsDescEditorReady(true);
    if (ref === iprvMesrEditorRef) setIsIprwEditorReady(true);
  };

  const handleEditorChange = (e, ref) => {
    const target = e?.editorName;
    const targetText = e?.editorTarget?.GetBodyValue();
    setErrors((prev) => ({ ...prev, [target]: false }));

    handleItemChange(target, targetText);
    ref.current?.editorRef.SaveBodyValue?.();
  };

  const handleFileSave = async (): Promise<string | null> => {
    const res = await dropzoneRef.current.saveFiles();
    if (res.fileSaveResult === FileSaveResult.FAIL) return null;

    const id = res.atchFileGrId ?? null;
    setCreateData((prev) => ({ ...prev, atchFileGrId: id }));
    return id;
  };

  useEffect(() => {
    if (isuId) {
      if (pageMode === PageMode.DELETE) return;
      (async () => {
        loadProject(isuId);
      })();
    }
    if (pageMode === PageMode.EDIT) findEqclId(createData.optValCtn1);
    if (pageMode === PageMode.WRITE) {
      setSearchParam((prev) => ({
        ...prev,
        searchOwnedPrjYn: 'Y',
      }));
    }
  }, [isuId, pageMode]);

  useEffect(() => {
    if (!searchParam?.searchOwnedPrjYn) return;
    findprojectList();
  }, [searchParam?.searchOwnedPrjYn]);

  useEffect(() => {
    if (props.isuId && pageMode !== PageMode.WRITE) {
      setIsuId(props.isuId);
      setPageMode(props.mode);
    }
  }, [props.isuId]);

  useEffect(() => {
    initCodes();
    if (isEmailFormat()) {
      //협력사 접속 시, 협력사 고정
      setCreateData((prev) => ({
        ...prev,
        excpId: deptCd,
        excpNm: deptNm,
      }));
    }

    if (isEditable()) {
      setCreateData((prev) => ({
        ...prev,
        dataInsUserNm: empNm,
        isuRegDt: prev.isuRegDt || moment().format('YYYYMMDD'),
      }));
    }
  }, []);

  useEffect(() => {
    if (pageMode !== PageMode.READ) {
      setCreateData((prev) => ({
        ...prev,
        isuOccrDivsCd: '',
      }));
    }
  }, [createData?.isuTpCd]);

  useEffect(() => {
    if (eqpNmYn) {
      setCreateData((prev) => ({
        ...prev,
        tgtEqpId: 'CMN',
        eqpId: '',
        eqpNm: '',
        eqclId: '',
        optValCtn1: '',
        prjSpCd: '',
        ...(!isEmailFormat()
          ? {
              excpId: '',
              excpNm: '',
            }
          : {}),
      }));
      if (props.excpId) {
        setCreateData((prev) => ({
          ...prev,
          excpId: props.excpId,
        }));
      }
    } else {
      setCreateData((prev) => ({
        ...prev,
        tgtEqpId: '',
      }));
    }
  }, [eqpNmYn]);

  useEffect(() => {
    checkPlantCd(createData.elmPrjId);
    setExcpResetKey((k) => k + 1);

    if (createData?.elmPrjId) {
      findExcpByElmPrjId(createData?.elmPrjId).then((result: IssueSetup[]) => {
        setPrjExcpData(result);
      });
    } else {
      setPrjExcpData([]);
    }
  }, [createData.elmPrjId]);

  useEffect(() => {
    findEqclId(createData.eqclId);
  }, [createData.eqclId, createData.eqpNm]);

  useEffect(() => {
    getCmnGrCdList();
  }, [cmnGrCd]);

  useEffect(() => {
    setCreateData((prev) => ({
      ...prev,
      rvwRltCtn: rvwRltCtn,
    }));
  }, [rvwRltCtn]);

  useEffect(() => {
    if (props.elmPrjId && props.excpId) {
      const plantCd = prjMasterData.find((item) => item.elmPrjId === props.elmPrjId);
      setCreateData((prev) => ({
        ...prev,
        elmPrjId: props.elmPrjId,
        excpId: props.excpId,
        bildPlntCd: plantCd?.bildPlntCd ?? '',
        bildPlntCdNm: plantCd?.bildPlntCdNm ?? '',
      }));
    }
  }, [props.elmPrjId, props.excpId, prjMasterData]);

  return (
    <CustomDialog title={getTitle()} open={props.open} onClose={props.close} size="xl">
      <TableContainer css={tb.table}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>{t('setup.label.플랜트', '플랜트')}</TableCell>
              <TableCell>
                {pageMode == PageMode.WRITE ? createData.bildPlntCdNm : originMasterData.bildPlntNm}
              </TableCell>
              <TableCell>
                <span className={isEditable() ? 'dot' : undefined}>
                  {t('setup.label.프로젝트명', '프로젝트명')}
                </span>
              </TableCell>
              <TableCell colSpan={3}>
                {pageMode == PageMode.WRITE ? (
                  props.elmPrjId ? (
                    optionsPrjMaster.find((item) => item.cmnCd === props.elmPrjId)?.cmnCdNm
                  ) : (
                    <ComboBox
                      placeholder={String(t('com.label.선택', '선택'))}
                      options={optionsPrjMaster}
                      defaultValue={originMasterData.elmPrjId}
                      onChange={(value) => {
                        setCreateData((prev) => ({
                          //프로젝트가 바뀌면 입력값 초기화
                          ...prev,
                          elmPrjId: value,
                          ...(!eqpNmYn ? { tgtEqpId: '' } : {}),
                          eqpId: '',
                          eqpNm: '',
                          eqclId: '',
                          isuCrgeUserId: '',
                          excpCrgeUserId: '',
                          ...(!isEmailFormat()
                            ? {
                                excpId: '',
                                excpNm: '',
                              }
                            : {}),
                        }));
                      }}
                      isError={errors?.elmPrjId}
                      msgError={String(
                        t('setup.msg.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                      )}
                    />
                  )
                ) : (
                  originMasterData.elmPrjNM
                )}
              </TableCell>
              <TableCell>{t('setup.label.상태', '상태')}</TableCell>
              <TableCell width="300px">{createData.isuStatNm}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className={isEditable() ? 'dot' : undefined}>
                  {t('setup.label.대상 설비', '대상 설비')}
                </span>
              </TableCell>
              <TableCell colSpan={3}>
                {pageMode == PageMode.WRITE ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <label>{t('com.label.공통', '공통')}</label>
                      <Checkbox
                        id="eqpNmYn"
                        name="eqpNmYn"
                        css={st.checkbox}
                        className="custom"
                        onClick={(e) => {
                          const checked = (e.target as HTMLInputElement).checked;
                          setEqpNmYn(checked);
                        }}
                        disableRipple
                      />
                    </div>
                    <CustomInputWithSearch
                      name="tgtEqpId"
                      className="find"
                      placeholder={String(
                        t('com.label.대상 설비를 입력해 주세요.', '대상 설비를 입력해 주세요.')
                      )}
                      value={createData.eqpNm}
                      onSearchClick={eqpNmYn ? undefined : handleOpenEquipmentModal}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          handleOpenEquipmentModal();
                        }
                      }}
                      isError={errors?.tgtEqpId}
                      msgError={String(
                        t('com.label.대상 설비를 선택해 주세요.', '대상 설비를 선택해 주세요.')
                      )}
                      readOnly={eqpNmYn}
                      style={{ width: '100%' }}
                    />
                  </div>
                ) : (
                  originMasterData.eqpNm
                )}
              </TableCell>
              <TableCell>
                <span
                  className={
                    isEditable() && !eqpNmYn && originMasterData?.eqpNm !== 'COMMON'
                      ? 'dot'
                      : undefined
                  }
                >
                  {t('setup.label.셋업 단계', '셋업 단계')}
                </span>
              </TableCell>
              <TableCell>
                {isEditable() && !eqpNmYn && originMasterData?.eqpNm !== 'COMMON' ? (
                  <ComboBox
                    key={`${createData.eqpId}-list`}
                    placeholder={String(t('com.label.선택', '선택'))}
                    options={cmnGrCdList}
                    defaultValue={originMasterData.prjSpCd}
                    onChange={(value) => {
                      setCreateData((prev) => ({ ...prev, prjSpCd: value }));
                    }}
                    isError={!eqpNmYn && errors?.prjSpCd}
                    msgError={String(
                      t('setup.msg.셋업 단계를 선택해 주세요.', '셋업 단계를 선택해 주세요.')
                    )}
                    readOnly={eqpNmYn}
                  />
                ) : (
                  originMasterData.prjSpNm
                )}
              </TableCell>
              <TableCell>
                <span className={isEditable() && eqpNmYn && !props.excpId ? 'dot' : undefined}>
                  {t('setup.label.협력업체명', '협력업체명')}
                </span>
              </TableCell>
              <TableCell>
                {isEditable() ? (
                  eqpNmYn && !isEmailFormat() && !props.excpId ? (
                    <ComboBox
                      key={excpResetKey}
                      defaultValue={originMasterData.excpId}
                      placeholder={String(t('com.label.선택', '선택'))}
                      options={optionsPrjExcp}
                      onChange={(value) => {
                        setCreateData((prev) => ({ ...prev, excpId: value }));
                      }}
                      isError={errors?.excpId}
                      msgError={String(
                        t('setup.msg.협력사명을 선택해 주세요.', '협력사명을 선택해 주세요.')
                      )}
                    />
                  ) : props.excpId ? (
                    optionsPrjExcp.find((item) => item.cmnCd === props.excpId)?.cmnCdNm
                  ) : (
                    createData.excpNm
                  )
                ) : (
                  originMasterData.excpNm
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className={isEditable() ? 'dot' : undefined}>
                  {t('setup.label.분류', '분류')}
                </span>
              </TableCell>
              <TableCell>
                {isEditable() ? (
                  <ComboBox
                    placeholder={String(t('com.label.선택', '선택'))}
                    options={isuTpCd}
                    defaultValue={originMasterData.isuTpCd}
                    onChange={(value) => {
                      setCreateData((prev) => ({ ...prev, isuTpCd: value }));
                    }}
                    isError={errors?.isuTpCd}
                    msgError={String(t('setup.msg.분류를 선택해 주세요.', '분류를 선택해 주세요.'))}
                  />
                ) : (
                  originMasterData.isuTpNm
                )}
              </TableCell>
              <TableCell>
                <span className={isEditable() ? 'dot' : undefined}>
                  {t('setup.label.구분', '구분')}
                </span>
              </TableCell>
              <TableCell>
                {isEditable() ? (
                  <ComboBox
                    key={`${createData?.isuTpCd}-isuOccrDivsCd`}
                    placeholder={String(t('com.label.선택', '선택'))}
                    options={filteredIsuOccrCd}
                    defaultValue={originMasterData.isuOccrDivsCd}
                    onChange={(value) => {
                      setCreateData((prev) => ({ ...prev, isuOccrDivsCd: value }));
                    }}
                    isError={errors?.isuOccrDivsCd}
                    msgError={String(t('setup.msg.구분을 선택해 주세요.', '구분을 선택해 주세요.'))}
                  />
                ) : (
                  originMasterData.isuOccrDivsNm
                )}
              </TableCell>
              <TableCell>
                <span className={isEditable() ? 'dot' : undefined}>
                  {t('setup.label.상세', '상세')}
                </span>
              </TableCell>
              <TableCell>
                {isEditable() ? (
                  <ComboBox
                    key={`${createData?.isuTpCd}-${createData?.isuOccrDivsCd}-isuOccrLocCd`}
                    placeholder={String(t('com.label.선택', '선택'))}
                    options={filteredIsuLocCd}
                    defaultValue={originMasterData.isuOccrLocCd}
                    onChange={(value) => {
                      setCreateData((prev) => ({ ...prev, isuOccrLocCd: value }));
                    }}
                    isError={errors?.isuOccrLocCd}
                    msgError={String(t('setup.msg.상세를 선택해 주세요.', '상세을 선택해 주세요.'))}
                  />
                ) : (
                  originMasterData.isuOccrLocNm
                )}
              </TableCell>
              <TableCell>
                <span className={isEditable() && createData.isuTpCd !== 'MINT' ? 'dot' : undefined}>
                  {t('setup.label.완료예정일자', '완료예정일자')}
                </span>
              </TableCell>
              <TableCell>
                {isEditable() ? (
                  <CustomDatepicker
                    isRange={false}
                    value={createData?.cpltExpcDt}
                    minDate={null}
                    onChange={(value) => {
                      const formatted = value ? dayjs(String(value)).format('YYYYMMDD') : '';
                      setCreateData((prev) => ({
                        ...prev,
                        cpltExpcDt: formatted,
                      }));
                    }}
                    isError={errors?.cpltExpcDt}
                    msgError={String(
                      t('com.msg.완료예정일자를 입력해 주세요.', '완료예정일자를 입력해 주세요.')
                    )}
                  />
                ) : (
                  dateFormat(originMasterData?.cpltExpcDt)
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className={isEditable() ? 'dot' : undefined}>
                  {t('setup.label.이슈명', '이슈명')}
                </span>
              </TableCell>
              <TableCell colSpan={5}>
                {isEditable() ? (
                  <CustomInputWithSearch
                    name="isuTitNm"
                    placeholder={String(
                      t('setup.label.이슈명을 입력해 주세요.', '이슈명을 입력해 주세요.')
                    )}
                    defaultValue={originMasterData.isuTitNm}
                    value={createData?.isuTitNm}
                    onChange={(e) => handleConditionChange(e)}
                    isError={errors?.isuTitNm}
                    msgError={String(
                      t('setup.msg.이슈명을 입력해 주세요.', '이슈명을 입력해 주세요.')
                    )}
                  />
                ) : (
                  originMasterData.isuTitNm
                )}
              </TableCell>
              <TableCell>
                <span className={isEditable() && createData.isuTpCd !== 'MINT' ? 'dot' : undefined}>
                  {t('setup.label.등록일자', '등록일자')}
                </span>
              </TableCell>
              <TableCell width="300px">
                {isEditable() ? (
                  <CustomDatepicker
                    isRange={false}
                    value={createData?.isuRegDt}
                    maxDate={new Date()}
                    onChange={(value) => {
                      const formatted = value ? dayjs(String(value)).format('YYYYMMDD') : '';
                      setCreateData((prev) => ({
                        ...prev,
                        isuRegDt: formatted,
                      }));
                    }}
                    isError={errors?.isuRegDt}
                    msgError={String(
                      t('com.msg.등록일자를 입력해 주세요.', '등록일자를 입력해 주세요.')
                    )}
                  />
                ) : (
                  dateFormat(originMasterData?.isuRegDt)
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className={isEditable() ? 'dot' : undefined}>
                  {t('setup.label.상세 내용', '상세 내용')}
                </span>
              </TableCell>
              <TableCell
                colSpan={7}
                style={{ height: '200px', verticalAlign: 'top', overflow: 'auto' }}
              >
                {isEditable() ? (
                  <CustomEditorInputWithSearch
                    editorRef={descEditorRef}
                    name={'prjIsuDesc'}
                    value={createData.prjIsuDesc}
                    isError={errors?.prjIsuDesc}
                    msgError={String(
                      t('com.msg.상세 내용을 입력해 주세요.', '상세 내용을 입력해 주세요.')
                    )}
                    onLoaded={() => handleEditorOnLoad(descEditorRef, createData.prjIsuDesc)}
                    onKeyActive={(e) => handleEditorChange(e, descEditorRef)}
                  />
                ) : (
                  <div
                    style={{
                      height: 'fit-content',
                      maxHeight: 500,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: CommonUtil.safeHtmlContents(originMasterData.prjIsuDesc, false),
                    }}
                  ></div>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('setup.label.진행 현황', '진행 현황')}</TableCell>
              <TableCell
                colSpan={7}
                style={{ height: '200px', verticalAlign: 'top', overflow: 'auto' }}
              >
                {isEditable() ? (
                  <CustomEditorInputWithSearch
                    editorRef={iprvMesrEditorRef}
                    name={'isuIprvMesrCtn'}
                    value={createData.isuIprvMesrCtn}
                    onLoaded={() =>
                      handleEditorOnLoad(iprvMesrEditorRef, createData.isuIprvMesrCtn)
                    }
                    onKeyActive={(e) => handleEditorChange(e, iprvMesrEditorRef)}
                  />
                ) : (
                  <div
                    style={{
                      height: 'fit-content',
                      maxHeight: 500,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      paddingRight: '8px',
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: CommonUtil.safeHtmlContents(originMasterData.isuIprvMesrCtn, false),
                    }}
                  ></div>
                )}
              </TableCell>
            </TableRow>
            {!isEditable() && originMasterData?.isuStatCd == 'CPLT' && (
              <TableRow>
                <TableCell>{t('setup.label.LGES Comment', 'LGES Comment')}</TableCell>
                <TableCell colSpan={7} style={{ height: '100px', verticalAlign: 'top' }}>
                  {originMasterData.rvwRltCtn}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell>{t('setup.label.등록자', '등록자')}</TableCell>
              <TableCell width="250px">{createData.dataInsUserNm}</TableCell>
              <TableCell>{t('setup.label.완료일자', '완료일자')}</TableCell>
              <TableCell>{dateFormat(createData?.cpltDt)}</TableCell>
              <TableCell>{t('setup.label.등록일자', '등록일자')}</TableCell>
              <TableCell>{dateFormat(createData?.dataInsDtm)}</TableCell>
              <TableCell>{t('setup.label.수정일자', '수정일자')}</TableCell>
              <TableCell>{dateFormat(createData?.dataUpdDtm)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('setup.label.첨부', '첨부')}</TableCell>
              <TableCell colSpan={7}>
                {isEditable() ? (
                  <Dropzone
                    ref={dropzoneRef}
                    downloadOnly={pageMode == 'READ'}
                    atchFileGrIdInput={createData?.atchFileGrId}
                    atchFileTpCd="SETUP_ISSUE"
                  />
                ) : (
                  <FileGrid atchFileGrId={originMasterData?.atchFileGrId || ''} />
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <GlobalBtnGroup>
        {isEditable() && (
          <Button
            css={IconButton.button}
            className="cancel"
            disableRipple
            onClick={pageMode === PageMode.EDIT ? btnCancel : props.close}
          >
            {t('com.button.취소', '취소')}
          </Button>
        )}
        {pageMode == PageMode.EDIT && createData.isuStatCd == 'SAVE' && (
          <Button
            key={'delete'}
            css={IconButton.button}
            className="delete"
            disableRipple
            onClick={() => btnSave('DELETE')}
          >
            {t('com.button.삭제', '삭제')}
          </Button>
        )}
        {((createData?.isuStatCd == 'SAVE' && pageMode == PageMode.EDIT) ||
          pageMode == PageMode.WRITE) && (
          <Button
            key="draft"
            css={IconButton.button}
            className="draft"
            disableRipple
            onClick={() => btnSave('SAVE')}
          >
            {t('com.button.임시저장', '임시저장')}
          </Button>
        )}
        {isEditable() ? (
          <>
            <Button
              key="save"
              css={IconButton.button}
              className="save"
              disableRipple
              onClick={() => {
                btnSave('REG_CPLT');
              }}
            >
              {t('com.button.저장', '저장')}
            </Button>
          </>
        ) : (
          originMasterData.isuStatCd !== 'CPLT' && (
            <>
              {isModify() && (
                <Button
                  css={IconButton.button}
                  className="write"
                  disableRipple
                  onClick={() => {
                    setPageMode(PageMode.EDIT);
                  }}
                >
                  {t('com.button.수정', '수정')}
                </Button>
              )}
              {!isEmailFormat() &&
                (originMasterData.isuStatCd == 'SAVE' || (
                  <Button
                    key="confirm"
                    css={IconButton.button}
                    className="confirm"
                    onClick={() => {
                      setIsClickConfirm(true);
                    }}
                  >
                    {t('com.button.완료', '완료')}
                  </Button>
                ))}
            </>
          )
        )}
      </GlobalBtnGroup>
      {isOpenEquipmentModal && (
        <SetupEquipmentPopUp
          open={isOpenEquipmentModal}
          close={() => setOpenEquipmentModal(false)}
          title={t('setup.label.설비추가', '설비추가')}
          condition={{
            elmPrjId: createData.elmPrjId as string,
            excpId: isEmailFormat()
              ? (createData.excpId as string)
              : props.excpId
              ? props.excpId
              : '',
          }}
          singleSelect
          defaultEquipment={createData.eqpNm as SetupEquipment}
          onCallback={(result) => {
            const equipments = (result || []) as SetupEquipment;
            setCreateData((prev) => ({
              ...prev,
              tgtEqpId: equipments.tgtEqpId,
              eqpId: equipments.eqpId,
              eqpNm: equipments.eqpNm,
              eqclId: equipments.eqclId,
              optValCtn1: equipments.eqclId,
              ...(!isEmailFormat()
                ? {
                    excpId: equipments.excpId,
                    excpNm: equipments.excpNm,
                  }
                : {}),
            }));
          }}
        />
      )}
      {isClickConfirm && (
        <IssueConfirmPopup
          open={isClickConfirm}
          close={() => setIsClickConfirm(false)}
          onConfirm={() => btnSave('CPLT')}
          onChangeComment={setRvwRltCtn}
        />
      )}
    </CustomDialog>
  );
};

export default ProjectIssueCreatePage;
